import { isAeoAllowed } from "@redotech/redo-aeo-common/is-aeo-allowed";
import { CATALOG_DATA_PERMISSION } from "@redotech/redo-merchant-app-common/catalog/catalog-access";
import { PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import { breadcrumbSlot } from "@redotech/redo-web/breadcrumb";
import { createCatalogProductsRoute } from "./catalog-products-route";
import { defaultChildRoute } from "./legacy-redirects";

const catalogProductsViewBreadcrumb = breadcrumbSlot();
const productFamilyDetailBreadcrumb = breadcrumbSlot();
const productDetailBreadcrumb = breadcrumbSlot();

const HidePaddingRoutes = [
  "dashboard",
  "prompts",
  "assistant",
  "competitors",
  "brand",
];
function shouldHidePadding(url: string) {
  return HidePaddingRoutes.some((path) => url.includes(path));
}

function aeoEnabledPermission({
  team,
}: {
  team: {
    settings: {
      aeo?: {
        enabled?: boolean;
        subscriptionBilling?: { enabled?: boolean };
        freeTrialEndDate?: Date | null;
      };
    };
  };
}): boolean {
  return isAeoAllowed(team.settings.aeo);
}

export const aeoRoutes: PermissionedRouteObject[] = [
  {
    path: "",
    handle: { breadcrumb: "AEO", permission: aeoEnabledPermission },
    async lazy() {
      const { Page } = await import("../../page");
      return { element: <Page hidePadding={shouldHidePadding} /> };
    },
    children: [
      defaultChildRoute("dashboard"),
      {
        path: "dashboard",
        handle: { breadcrumb: "Dashboard" },
        async lazy() {
          const { AeoDashboard } =
            await import("../../../aeo/dashboard/dashboard");
          return { Component: AeoDashboard };
        },
      },
      {
        path: "prompts",
        handle: { breadcrumb: "Prompts" },
        children: [
          {
            index: true,
            async lazy() {
              const { AeoPromptsPage } =
                await import("../../../aeo/prompt/prompts-page");
              return { Component: AeoPromptsPage };
            },
          },
          {
            path: ":promptId",
            handle: { breadcrumb: "Prompt Details" },
            async lazy() {
              const { PromptResponsesLayout } =
                await import("../../../aeo/prompt-response/prompt-responses-layout");
              return { Component: PromptResponsesLayout };
            },
            children: [
              {
                index: true,
                async lazy() {
                  const { PromptResponsesPage } =
                    await import("../../../aeo/prompt-response/prompt-responses-page");
                  return { Component: PromptResponsesPage };
                },
              },
            ],
          },
        ],
      },
      {
        path: "competitors",
        handle: { breadcrumb: "Competitors" },
        async lazy() {
          const { AeoCompetitorsPage } =
            await import("../../../aeo/competitors/competitors-page");
          return { Component: AeoCompetitorsPage };
        },
      },
      {
        path: "brand",
        handle: { breadcrumb: "Brand" },
        async lazy() {
          const { AeoBrandOverviewPage } =
            await import("../../../aeo/brand-overview-page");
          return { Component: AeoBrandOverviewPage };
        },
      },
      {
        path: "integrations",
        handle: { breadcrumb: "Integrations" },
        async lazy() {
          const { AeoIntegrationsPage } =
            await import("../../../aeo/integrations/integrations-page");
          return { Component: AeoIntegrationsPage };
        },
      },
      {
        path: "assistant/:threadId?",
        handle: { breadcrumb: "Assistant" },
        async lazy() {
          const { AeoAssistantPage } =
            await import("../../../aeo/aeo-assistant/aeo-assistant-page");
          return { Component: AeoAssistantPage };
        },
      },
      {
        path: "catalog",
        handle: { breadcrumb: "Catalog", permission: CATALOG_DATA_PERMISSION },
        children: [
          defaultChildRoute("products"),
          createCatalogProductsRoute({
            catalogProductsViewBreadcrumb,
            productDetailBreadcrumb,
            productFamilyDetailBreadcrumb,
            showProductModal: false,
          }),
          {
            path: "optimization",
            handle: { breadcrumb: "Optimization" },
            async lazy() {
              const { ListingOptimizationPage } =
                await import("@redotech/redo-merchant-inventory-app/listing-optimizations/listing-optimization-page");
              return { Component: ListingOptimizationPage };
            },
          },
        ],
      },
    ],
  },
];
